import React, { useState, useEffect } from "react";

const PaymentMethodForm = ({ method, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "both",
    logo: "",
    description: "",
    instructions: "",
    accountDetails: {},
    order: 0,
    isActive: true
  });

  const [accountFields, setAccountFields] = useState([{ key: "", value: "" }]);

  useEffect(() => {
    if (method) {
      setFormData({
        name: method.name || "",
        type: method.type || "both",
        logo: method.logo || "",
        description: method.description || "",
        instructions: method.instructions || "",
        accountDetails: method.accountDetails || {},
        order: method.order || 0,
        isActive: method.isActive !== undefined ? method.isActive : true
      });

      // Populate accountFields if accountDetails exist
      if (method.accountDetails && Object.keys(method.accountDetails).length > 0) {
        const fields = Object.entries(method.accountDetails).map(([key, value]) => ({
          key,
          value
        }));
        setAccountFields(fields);
      }
    }
  }, [method]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleAccountFieldChange = (index, field, value) => {
    const updatedFields = [...accountFields];
    updatedFields[index][field] = value;
    setAccountFields(updatedFields);
  };

  const handleAddField = () => {
    setAccountFields((prev) => [...prev, { key: "", value: "" }]);
  };

  const handleRemoveField = (index) => {
    setAccountFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert key-value pairs into an object
    const accountDetails = {};
    for (let { key, value } of accountFields) {
      if (key.trim()) {
        accountDetails[key.trim()] = value.trim();
      }
    }

    const submitData = {
      ...formData,
      accountDetails
    };

    onSubmit(submitData);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <h2 className="text-xl font-semibold mb-6 text-gray-800">
        {method ? "Edit Payment Method" : "Create New Payment Method"}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* --- Existing fields unchanged --- */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 bg-white text-gray-800 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Bank Transfer, PayPal"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">Type *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 bg-white text-gray-800 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="deposit">Deposit Only</option>
              <option value="withdrawal">Withdrawal Only</option>
              <option value="both">Both Deposit & Withdrawal</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">Logo (Emoji) *</label>
            <input
              type="text"
              name="logo"
              value={formData.logo}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 bg-white text-gray-800 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 🏦, 💳, 🔵"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">Display Order</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full border border-gray-300 bg-white text-gray-800 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">Description *</label>
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 bg-white text-gray-800 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Short description shown to users"
          />
        </div>

        {/* Instructions */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">Instructions</label>
          <textarea
            name="instructions"
            value={formData.instructions}
            onChange={handleChange}
            rows="3"
            className="w-full border border-gray-300 bg-white text-gray-800 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Detailed instructions for users..."
          />
        </div>

        {/* 🔹 Account Details (Key-Value Pair Inputs) */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Account Details
          </label>
          <div className="space-y-3">
            {accountFields.map((field, index) => (
              <div key={index} className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Key (e.g., bankName)"
                  value={field.key}
                  onChange={(e) => handleAccountFieldChange(index, "key", e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
                <input
                  type="text"
                  placeholder="Value (e.g., Example Bank)"
                  value={field.value}
                  onChange={(e) => handleAccountFieldChange(index, "value", e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveField(index)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 rounded-lg font-medium"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddField}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-1 rounded-lg font-medium"
            >
              + Add Field
            </button>
          </div>
        </div>

        {/* Active Status */}
        <div className="flex items-center">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label className="ml-2 block text-gray-700 font-medium">
            Active (visible to users)
          </label>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm"
          >
            {method ? "Update" : "Create"} Payment Method
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentMethodForm;
